import { NetworkType, zeroExWrapperUrl } from 'abis/set/sets'
import Big from 'big.js'
import { atom } from 'jotai'
import { CAI, JMX } from 'products'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_QUOTES } from 'services/query-v2/keys'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { convertAmountToWei, objToQueryString } from 'utils'
import { isAddressEqual, zeroAddress } from 'viem'
import type { RawQuotesResponse } from '../output/model'
import {
  type QuoteParams,
  type QuotesRequestAtoms,
  type QuotesResponse,
  defaultQuoteParams,
  defaultQuoteResponse,
  parseQuoteResponse,
} from './model'
import { notifyTransactionLiquidityError } from 'components'
import { ZERO_EX_AFFILIATE_AVAX_ADDRESS, ZERO_EX_AFFILIATE_BASE_ADDRESS } from 'app-constants'

export const quotesQuery = async (params: QuoteParams) => {
  const url = zeroExWrapperUrl
  const inactiveProduct =
    !isAddressEqual(params.buyCurrency.address, CAI.address) &&
    !isAddressEqual(params.sellCurrency.address, CAI.address) &&
    !isAddressEqual(params.buyCurrency.address, JMX.address) &&
    !isAddressEqual(params.sellCurrency.address, JMX.address)
  if (
    !params.account ||
    isAddressEqual(params.account, zeroAddress) ||
    inactiveProduct ||
    params.sellAmount.eq(0)
  ) {
    return defaultQuoteResponse
  }

  const amount = convertAmountToWei(params.sellAmount, params.sellCurrency.decimals)
  const adjustedAmount = amount.gt(0)
    ? amount
    : convertAmountToWei(Big('1'), params.sellCurrency.decimals)

  const urlParams = {
    chainId: params.chainId,
    sellToken: isAddressEqual(params.sellCurrency.address, zeroAddress)
      ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
      : params.sellCurrency.address,
    buyToken: isAddressEqual(params.buyCurrency.address, zeroAddress)
      ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
      : params.buyCurrency.address,
    sellAmount: adjustedAmount.toFixed(),
    affiliateAddress: params.affiliateAddress,
    slippageBps: params.slippagePercentage * 1000,
    taker: params.account,
    txOrigin: params.account,
    swapFeeToken: isAddressEqual(params.sellCurrency.address, zeroAddress)
      ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
      : params.sellCurrency.address,
    swapFeeBps: 30,
    swapFeeRecipient:
      params.chainId === NetworkType.Avalanche
        ? ZERO_EX_AFFILIATE_AVAX_ADDRESS
        : params.chainId === NetworkType.Base
          ? ZERO_EX_AFFILIATE_BASE_ADDRESS
          : undefined,
  }

  const response = await fetch(`${url}/swap/allowance-holder/quote?${objToQueryString(urlParams)}`)
  const json = (await response.json()) as RawQuotesResponse | { liquidityAvailable: false }
  if (!(json as { liquidityAvailable: false }).liquidityAvailable) {
    notifyTransactionLiquidityError(
      `${params.sellCurrency.address}-${params.sellAmount.toString()}`,
    )
  }

  return parseQuoteResponse(json as RawQuotesResponse, params)
}

const quotesQueryPlaceholder = async <T>({
  queryKey: [, params],
}: {
  queryKey: [string, QuoteParams]
}) => {
  return quotesQuery(params) as T
}

export const indexV1QuoteAtomsFactory = () => {
  const zeroExParamsAtom = atom<QuoteParams>(defaultQuoteParams)

  const mainProperties = createAtomsWithQuery<
    QuotesResponse,
    QuoteParams,
    null,
    null,
    [string, QuoteParams]
  >(
    { first: zeroExParamsAtom },
    QUERY_KEY_QUOTES,
    quotesQueryPlaceholder<QuotesResponse>,
    DEFAULT_REFETCH_INTERVAL,
  )

  return {
    params: zeroExParamsAtom,
    ...mainProperties,
  } as QuotesRequestAtoms
}
